import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './providers/ThemeProvider';
import { ThemeProvider as AppThemeProvider } from './contexts/ThemeContext';
import { theme as appTheme } from './theme/theme';
import App from './App';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import CheckoutPage from './pages/CheckoutPage';
import OrderDetailsPage from './pages/OrderDetailsPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import OrdersPage from './pages/OrdersPage';
import CustomerHomePage from './pages/CustomerHomePage';
import ExploreShopsPage from './pages/ExploreShopsPage';
import BrowseAllPage from './pages/BrowseAllPage';
import ProductsPage from './pages/ProductsPage';
import SearchPage from './pages/SearchPage';
import ShopPage from './pages/ShopPage';
import ShopDetailsPage from './pages/ShopDetailsPage';
import WishlistPage from './pages/WishlistPage';
import LoyaltyPointsPage from './pages/LoyaltyPointsPage';
import FavoriteShopsPage from './pages/FavoriteShopsPage';
import NotificationsPage from './pages/NotificationsPage';
import QuickoPlusPage from './pages/QuickoPlusPage';
import WeeklyAdPage from './pages/WeeklyAdPage';
import CouponsPage from './pages/CouponsPage';
import ThemePreviewPage from './pages/ThemePreviewPage';
import ThemeSettingsPage from './pages/ThemeSettingsPage';
import ServicesHomePage from './pages/ServicesHomePage';
import ServiceProvidersPage from './pages/ServiceProvidersPage';
import ServiceProviderProfilePage from './pages/ServiceProviderProfilePage';
import ServiceBookingPage from './pages/ServiceBookingPage';
import ServiceBookingConfirmationPage from './pages/ServiceBookingConfirmationPage';
import MyServiceBookingsPage from './pages/MyServiceBookingsPage';
import ProviderOnboardingPage from './pages/ProviderOnboardingPage';
import ProtectedRoute from './components/ProtectedRoute';
import TokenValidator from './components/TokenValidator';

const muiTheme = createTheme({
  palette: {
    primary: {
      main: appTheme.colors.primary,
      dark: appTheme.colors.primaryDark,
      light: appTheme.colors.primaryLight,
    },
    secondary: {
      main: appTheme.colors.secondary,
      dark: appTheme.colors.secondaryDark,
      light: appTheme.colors.secondaryLight,
    },
    error: {
      main: appTheme.colors.error,
    },
    success: {
      main: appTheme.colors.success,
    },
    warning: {
      main: appTheme.colors.warning,
    },
    info: {
      main: appTheme.colors.info,
    },
    background: {
      default: appTheme.colors.background,
      paper: appTheme.colors.surface,
    },
    text: {
      primary: appTheme.colors.text,
      secondary: appTheme.colors.textSecondary,
    },
  },
  typography: {
    fontFamily: "'Poppins', 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
    h1: {
      fontSize: `${appTheme.typography.h1}px`,
      fontWeight: Number(appTheme.typography.bold),
      lineHeight: appTheme.typography.lineHeight.tight,
    },
    h2: {
      fontSize: `${appTheme.typography.h2}px`,
      fontWeight: Number(appTheme.typography.bold),
      lineHeight: appTheme.typography.lineHeight.normal,
    },
    h3: {
      fontSize: `${appTheme.typography.h3}px`,
      fontWeight: Number(appTheme.typography.bold),
      lineHeight: appTheme.typography.lineHeight.normal,
    },
    h4: {
      fontSize: `${appTheme.typography.h4}px`,
      fontWeight: Number(appTheme.typography.bold),
      lineHeight: appTheme.typography.lineHeight.normal,
    },
    body1: {
      fontSize: `${appTheme.typography.body}px`,
      fontWeight: Number(appTheme.typography.regular),
      lineHeight: appTheme.typography.lineHeight.body,
    },
    button: {
      fontSize: `${appTheme.typography.button}px`,
      fontWeight: Number(appTheme.typography.semiBold),
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: appTheme.borderRadius.md,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: appTheme.colors.background,
          background: appTheme.colors.background,
          color: appTheme.colors.text,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: appTheme.borderRadius.md,
          textTransform: 'none',
          fontWeight: Number(appTheme.typography.semiBold),
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: appTheme.borderRadius.lg,
          boxShadow: appTheme.shadows.card,
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <AppThemeProvider>
      <ThemeProvider>
        <MuiThemeProvider theme={muiTheme}>
          <BrowserRouter>
            <AuthProvider>
              <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              
              {/* Shop Discovery Routes */}
              <Route path="/explore-shops" element={<ExploreShopsPage />} />
              <Route path="/shops" element={<ExploreShopsPage />} />
              
              {/* Browse All Products Route */}
              <Route path="/browse-all" element={<BrowseAllPage />} />
              <Route path="/browse" element={<BrowseAllPage />} />
              
              {/* Products Categories Route */}
              <Route path="/products" element={<ProductsPage />} />
              
              {/* Search Route */}
              <Route path="/search" element={<SearchPage />} />
              
                     {/* Shop Page Route */}
                     <Route path="/shop/:shopId" element={<ShopPage />} />
                     {/* Shop Details Page Route (details only, no products) */}
                     <Route path="/shop/:shopId/details" element={<ShopDetailsPage />} />
              
              <Route path="/profile" element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              } />
              <Route path="/" element={
                <TokenValidator>
                  <App />
                </TokenValidator>
              } />
              <Route path="/customer" element={<CustomerHomePage />} />
              <Route path="/customer/home" element={<CustomerHomePage />} />
              <Route path="/wishlist" element={
                <ProtectedRoute>
                  <WishlistPage />
                </ProtectedRoute>
              } />
              <Route path="/loyalty" element={
                <ProtectedRoute>
                  <LoyaltyPointsPage />
                </ProtectedRoute>
              } />
              <Route path="/favorite-shops" element={
                <ProtectedRoute>
                  <FavoriteShopsPage />
                </ProtectedRoute>
              } />
              <Route path="/notifications" element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              } />
              <Route path="/quickko-plus" element={<QuickoPlusPage />} />
              <Route path="/weekly-ad" element={<WeeklyAdPage />} />
              <Route path="/coupons" element={<CouponsPage />} />
              <Route path="/app" element={
                <TokenValidator>
                  <App />
                </TokenValidator>
              } />
              <Route path="/checkout" element={
                <ProtectedRoute>
                  <CheckoutPage />
                </ProtectedRoute>
              } />
              <Route path="/orders" element={
                <ProtectedRoute>
                  <OrdersPage />
                </ProtectedRoute>
              } />
              <Route path="/orders/:orderId" element={
                <ProtectedRoute>
                  <OrderDetailsPage />
                </ProtectedRoute>
              } />
              <Route path="/order-confirmation/:orderId" element={<OrderConfirmationPage />} />
              
              {/* Theme Routes */}
              <Route path="/theme-preview" element={<ThemePreviewPage />} />
              <Route path="/theme-settings" element={<ThemeSettingsPage />} />
              
              {/* Services Marketplace Routes */}
              <Route path="/services" element={<ServicesHomePage />} />
              <Route path="/services/:categoryId" element={<ServiceProvidersPage />} />
              <Route path="/services/provider/:providerId" element={<ServiceProviderProfilePage />} />
              <Route path="/services/book/:providerId" element={
                <ProtectedRoute>
                  <ServiceBookingPage />
                </ProtectedRoute>
              } />
              <Route path="/services/booking-confirmation/:providerId" element={
                <ProtectedRoute>
                  <ServiceBookingConfirmationPage />
                </ProtectedRoute>
              } />
              <Route path="/services/my-bookings" element={
                <ProtectedRoute>
                  <MyServiceBookingsPage />
                </ProtectedRoute>
              } />
              <Route path="/services/onboard" element={<ProviderOnboardingPage />} />
              <Route path="/services/emergency" element={<ServiceProvidersPage />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </MuiThemeProvider>
    </ThemeProvider>
    </AppThemeProvider>
  </React.StrictMode>
); 